import { config } from '@skytech/config'
import type { ValueLabelPair } from '@skytech/ui'
import dayjs from 'dayjs'

const isDevelopmentMode = config.ENVIRONMENT === 'dev'
export const CURRENT_YEAR = new Date().getFullYear()

export const yearOptions = (initialYear = 2018): ValueLabelPair[] => {
  let options: ValueLabelPair[] = []
  for (let i = CURRENT_YEAR; i >= initialYear; i--) {
    options.push({
      value: i,
      label: i.toString(),
    })
  }
  return options
}

// first and last day of previous month
export const getDefaultReportDates = () => {
  // for development env and to get some data
  const date = isDevelopmentMode ? new Date('2022-02-01') : new Date()
  const firstDay = new Date(date.getFullYear(), date.getMonth() - 1, 1)
  const lastDay = new Date(date.getFullYear(), date.getMonth(), 0)
  return [dayjs(firstDay).format('YYYY-MM-DD'), dayjs(lastDay).format('YYYY-MM-DD')]
}

export function downloadTextFile(text: string, filename: string) {
  const blob = new Blob([text], { type: 'text/plain' })

  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(blob)
  anchor.download = filename

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

export function capitalizeStr(str: string | undefined) {
  if (!str) return ''
  return str.replace(/[_-]/g, ' ').replace(/\w\S*/g, w => w.replace(/^\w/, c => c.toUpperCase()))
}

// Define a type RequiredKeys<T> which extracts keys from type T that are required (non-optional).
// It checks if any key in T is optional (?), and if so, excludes it from the resulting type.
type RequiredKeys<T> = {
  [K in keyof T]-?: {} extends Pick<T, K> ? never : K
}[keyof T]

type ObjectWithKeys<T> = Pick<T, RequiredKeys<T>>

export function groupAndSumByKeys<T extends ObjectWithKeys<T>>(
  data: T[],
  groupingKeys: (keyof T)[],
  sumKeys?: (keyof T)[]
): T[] {
  const groupedData: Record<string, any> = {}

  // Group the data by grouping keys and initialize sum keys with 0
  data.forEach(item => {
    const groupKey = groupingKeys.map(key => item[key]).join('|')

    if (!groupedData[groupKey]) {
      groupedData[groupKey] = { ...item }
      if (sumKeys && sumKeys.length > 0) {
        sumKeys.forEach(key => {
          groupedData[groupKey][key] = 0.0
        })
      }
    }

    if (sumKeys && sumKeys.length > 0) {
      sumKeys.forEach(key => {
        groupedData[groupKey][key] += parseFloat(item[key])
      })
    }
  })

  // Convert the grouped data to an array of objects
  return Object.values(groupedData)
}
